import Nav from "../components/Nav";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import { useEffect, useState } from "react";
import { Check } from "lucide-react";

export default function Billing() {
  const { user, refresh } = useAuth();
  const [loading, setLoading] = useState(false);
  const [sub, setSub] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/subscription/status");
        setSub(data);
      } catch (e) {
        /* ignore */
      }
    })();
  }, []);

  const subscribe = () => {
    const base = process.env.REACT_APP_STRIPE_PAYMENT_LINK;
    if (!base) {
      toast.error("Subscription link isn't set up yet.");
      return;
    }
    setLoading(true);
    const url = `${base}?client_reference_id=${encodeURIComponent(user.id)}&prefilled_email=${encodeURIComponent(user.email)}`;
    window.location.href = url;
  };

  const cancel = async () => {
    setLoading(true);
    try {
      await api.post("/subscription/cancel");
      await refresh();
      const { data } = await api.get("/subscription/status");
      setSub(data);
      toast.success("Subscription canceled. You can come back anytime.");
    } catch (e) {
      toast.error("Couldn't cancel");
    } finally {
      setLoading(false);
    }
  };

  const status = user?.subscription_status;
  const isActive = status === "active";
  const isTrial = status === "trialing" && sub?.has_access;

  return (
    <div className="min-h-screen bg-[#F4F7F6]">
      <Nav />
      <div className="max-w-3xl mx-auto px-6 py-10">
        <h1 className="font-display text-4xl text-slate-800">Billing</h1>
        <p className="text-slate-600 mt-1">Simple, weekly, no games.</p>

        <div className="mt-8 grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm">
            <span className="uppercase tracking-[0.15em] text-xs font-semibold text-slate-500">Current plan</span>
            <h2 className="font-display text-3xl text-slate-800 mt-2">Zaf • Weekly</h2>
            <div className="mt-4 flex items-baseline gap-1">
              <span className="font-display text-5xl text-[#2E9D96]">$6.49</span>
              <span className="text-slate-500 font-semibold">/ week</span>
            </div>
            <p className="mt-2 text-slate-500 text-sm">
              Status: <span className="font-semibold text-slate-700" data-testid="billing-status">{status || "—"}</span>
            </p>
            {isTrial && user?.trial_ends_at && (
              <p className="mt-1 text-slate-500 text-sm">Free trial ends: {new Date(user.trial_ends_at).toLocaleString()}</p>
            )}

            <div className="mt-6 flex gap-3">
              {isActive ? (
                <button
                  onClick={cancel}
                  disabled={loading}
                  className="px-6 py-2.5 rounded-full border border-slate-200 text-slate-700 hover:bg-slate-50 font-semibold transition-colors"
                  data-testid="billing-cancel"
                >
                  Cancel subscription
                </button>
              ) : (
                <button
                  onClick={subscribe}
                  disabled={loading}
                  className="px-7 py-2.5 rounded-full bg-[#2E9D96] hover:bg-[#1F7B75] disabled:opacity-60 text-white font-semibold transition-colors"
                  data-testid="billing-subscribe"
                >
                  {loading ? "Opening…" : isTrial ? "Subscribe now" : "Subscribe — $6.49/week"}
                </button>
              )}
            </div>
          </div>

          <div className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm">
            <h3 className="font-display text-2xl text-slate-800">What's included</h3>
            <ul className="mt-4 space-y-3 text-slate-700">
              {[
                "Unlimited chat with Zaf",
                "Daily wellness check-ins",
                "Trusted-contact safety alerts",
                "Photo sharing in chat",
                "Private memory — Zaf remembers you",
              ].map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <Check className="text-[#2E9D96] mt-0.5" size={18} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 text-xs text-slate-400">
              Payments run through Stripe in test mode. Use card 4242 4242 4242 4242, any future expiry and CVC.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
